import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IoMdMegaphone } from 'react-icons/io';
import { FiUser, FiBell, FiEye, FiCheck, FiAlertCircle, FiFileText } from 'react-icons/fi';
import { ModalShell } from './CampaignModals';
import { Template } from '../../../model/Template';
import { Thumbnail, previewPath, welcomeTemplateName } from './EmailTemplates';
import { toast } from '../Toast';

export type TemplateRole = 'announcement' | 'welcome' | 'notifications';

// notifications use the per-stock AnnouncementCommentTemplate wrapper, not a library template
const investiDefaultId = '__investi-default';
const customNotificationId = '__custom-notification';

interface RoleCopy { tab: string; icon: React.ReactNode; title: string; sub: string; note: string }
const copy: Record<TemplateRole, RoleCopy> = {
    announcement: {
        tab: 'ASX Announcements', icon: <IoMdMegaphone />, title: 'Which Template Should ASX Announcements Use?',
        sub: 'Every announcement email will look like the one you pick. It takes effect from the next announcement.',
        note: 'Announcement emails already sent keep the template they were sent with. This only changes what happens from now on.'
    },
    welcome: {
        tab: 'Welcome Email', icon: <FiUser />, title: 'Which Template Should the Welcome Email Use?',
        sub: 'Every holder you invite to the Website Hub gets the one you pick. It takes effect from the next invite.',
        note: 'Welcome emails already sent keep the template they were sent with. This only changes what happens from now on.'
    },
    notifications: {
        tab: 'Notifications', icon: <FiBell />, title: 'Which Template Should Notifications Use?',
        sub: 'Confirm your email, question answered. It takes effect from the next notification.',
        note: 'Notifications already sent keep the template they were sent with. This only changes what happens from now on.'
    }
};
const roles: TemplateRole[] = ['announcement', 'welcome', 'notifications'];

interface Option { id: string; name: string; sub: string; template?: Template }

// spec 598:5499 (note 604:5961). Real: templates, the announcement template (saved via onSaveAnnouncement), the "Welcome Email"
// template, custom vs default notification wrapper. Welcome / Notifications have no assignable source → Save is preview only.
// Omitted: "Ready-made / Yours · used N times" (no data).
const SetRoleTemplateModal: React.FC<{
    initialRole: TemplateRole; templates: Template[]; announcementTemplateId: string; hasCustomNotification: boolean;
    onClose: () => void; onSaveAnnouncement: (id: string) => Promise<boolean>;
}> = ({ initialRole, templates, announcementTemplateId, hasCustomNotification, onClose, onSaveAnnouncement }) => {
    const history = useHistory();
    const welcomeId = templates.find(t => t.name === welcomeTemplateName)?.id || '';
    const currentOf: Record<TemplateRole, string> = {
        announcement: announcementTemplateId,
        welcome: welcomeId,
        notifications: hasCustomNotification ? customNotificationId : investiDefaultId
    };
    const [role, setRole] = useState<TemplateRole>(initialRole);
    const [selected, setSelected] = useState<Record<TemplateRole, string>>(currentOf);
    const [saving, setSaving] = useState(false);

    const fromTemplate = (t: Template): Option => ({ id: t.id!, name: t.name, sub: t.isForCustomCampaign ? 'For campaigns you write yourself' : '', template: t });
    const library = templates.filter(t => t.name !== welcomeTemplateName).map(fromTemplate);
    const optionsOf: Record<TemplateRole, Option[]> = {
        announcement: library,
        welcome: templates.map(fromTemplate),
        notifications: [
            ...(hasCustomNotification ? [{ id: customNotificationId, name: 'Your custom notification layout', sub: 'Set up for this client' }] : []),
            { id: investiDefaultId, name: 'Investi default', sub: 'Already tested for every email app' },
            ...library
        ]
    };

    const current = currentOf[role];
    const choice = selected[role];

    // assigned to another role — shown so a template isn't reused by accident
    const badge = (o: Option) => {
        if (o.id === current) return <span className="srt-badge on">Assigned · in use now</span>;
        if (o.id === announcementTemplateId) return <span className="srt-badge">Used for ASX announcements</span>;
        if (o.id === welcomeId) return <span className="srt-badge">Used for Welcome email</span>;
        return <span className="srt-badge">Not assigned</span>;
    };

    const save = async () => {
        if (role !== 'announcement') {
            toast.info(`Preview only: the ${role === 'welcome' ? 'welcome email' : 'notifications'} template can’t be changed yet, so nothing was saved`);
            onClose();
            return;
        }
        setSaving(true);
        const ok = await onSaveAnnouncement(choice);
        if (ok) onClose(); else setSaving(false);
    };

    const c = copy[role];
    return <ModalShell icon={c.icon} title={c.title} sub={c.sub} note="Notifications can also use the Investi default, which is already tested for every email app." onClose={onClose}
        buttons={<>
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={!choice || choice === current || saving}>{saving ? 'Saving…' : 'Use This Template'}</button>
        </>}>
        <div className="set-role-template">
            <div className="srt-tabs" role="tablist">
                {roles.map(r => <button key={r} type="button" role="tab" aria-selected={r === role} className={`srt-tab${r === role ? ' on' : ''}`} onClick={() => setRole(r)}>{copy[r].tab}</button>)}
            </div>
            {optionsOf[role].length === 0 && <p className="srt-empty">No templates yet</p>}
            {/* div, not label: a label forwards clicks to its first control, which is the Preview button */}
            {optionsOf[role].map(o => <div key={o.id} className={`srt-option${o.id === choice ? ' on' : ''}`} onClick={() => setSelected({ ...selected, [role]: o.id })}>
                {o.template ? <Thumbnail html={o.template.html} className="srt-thumb" /> : <span className="srt-tile"><FiFileText /></span>}
                <span className="srt-t">
                    <span className="srt-name">{o.name}</span>
                    {o.sub && <span className="srt-sub">{o.sub}</span>}
                </span>
                {badge(o)}
                {o.template && <button type="button" className="srt-preview" onClick={e => { e.stopPropagation(); onClose(); history.push(previewPath(o.id)); }}><FiEye /> Preview</button>}
                <input type="radio" name="role-template" checked={o.id === choice} onChange={() => setSelected({ ...selected, [role]: o.id })} />
                <span className="srt-radio">{o.id === choice && <FiCheck />}</span>
            </div>)}
            <div className="srt-note"><FiAlertCircle /> {c.note}</div>
        </div>
    </ModalShell>;
};

export default SetRoleTemplateModal;
